import { useState, useEffect } from 'react';
import { caixaService } from '../../services';
import { useCaixa } from '../../context/CaixaContext';
import { formatCurrency, formatDateTime } from '../../utils/format';
import './Caixa.css';

export default function Caixa() {
  const { recarregar: recarregarCaixaGlobal } = useCaixa();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saldoInicial, setSaldoInicial] = useState('');
  const [sangria, setSangria] = useState({ valor: '', motivo: '' });
  const [suprimento, setSuprimento] = useState({ valor: '', descricao: '' });

  // Fechamento de caixa (conferência)
  const [modalFechar, setModalFechar] = useState(false);
  const [valorContado, setValorContado] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [fechando, setFechando] = useState(false);
  const [resultadoFechamento, setResultadoFechamento] = useState(null);
  const [atualizando, setAtualizando] = useState(false);

  const carregar = () => {
    caixaService.atual()
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  const abrirCaixa = async () => {
    try {
      await caixaService.abrir(parseFloat(saldoInicial) || 0);
      setSaldoInicial('');
      carregar();
      recarregarCaixaGlobal();
    } catch {}
  };

  // Total de movimentações (vendas + suprimentos - sangrias) do caixa aberto
  const totalMovimentacoes = (data?.movimentacoes || [])
    .reduce((s, m) => s + parseFloat(m.valor || 0), 0);
  const saldoInicialAtual = parseFloat(data?.caixa?.saldo_inicial || 0);
  const saldoEsperado = saldoInicialAtual + totalMovimentacoes;
  const diferencaPrevia = valorContado !== ''
    ? (parseFloat(valorContado) || 0) - saldoEsperado
    : null;

  // Resumo por forma: bruto x líquido (descontadas as taxas de cartão)
  const resumoFormas = data?.resumo_formas || [];
  const totalBrutoVendas = resumoFormas.reduce((s, r) => s + parseFloat(r.total || 0), 0);
  const totalLiquidoVendas = resumoFormas.reduce((s, r) => s + parseFloat(r.total_liquido ?? r.total ?? 0), 0);
  const totalTaxasCartao = Math.round((totalBrutoVendas - totalLiquidoVendas) * 100) / 100;

  const abrirModalFechar = () => {
    setValorContado('');
    setObservacoes('');
    setResultadoFechamento(null);
    setModalFechar(true);
  };

  const confirmarFecharCaixa = async () => {
    setFechando(true);
    try {
      const { data: res } = await caixaService.fechar({
        valor_contado: parseFloat(valorContado) || 0,
        observacoes: observacoes.trim() || null,
      });
      setResultadoFechamento(res);
      recarregarCaixaGlobal();
    } catch {
      alert('Erro ao fechar o caixa');
    } finally {
      setFechando(false);
    }
  };

  const fecharModalEConcluir = () => {
    setModalFechar(false);
    setResultadoFechamento(null);
    carregar();
  };

  const enviarSangria = async () => {
    try {
      await caixaService.sangria(sangria);
      setSangria({ valor: '', motivo: '' });
      carregar();
    } catch {}
  };

  const enviarSuprimento = async () => {
    try {
      await caixaService.suprimento(suprimento);
      setSuprimento({ valor: '', descricao: '' });
      carregar();
    } catch {}
  };

  // Força o tablet/navegador a baixar a última versão publicada, sem precisar
  // reiniciar o aparelho: limpa o cache (Cache API + WebView do Fully) e recarrega.
  const atualizarAplicacao = async () => {
    setAtualizando(true);
    try {
      if (window.caches && window.caches.keys) {
        const chaves = await window.caches.keys();
        await Promise.all(chaves.map((k) => window.caches.delete(k)));
      }
      if (window.fully && typeof window.fully.clearCache === 'function') {
        window.fully.clearCache();
      }
    } catch {}
    const url = new URL(window.location.href);
    url.searchParams.set('v', Date.now().toString());
    window.location.replace(url.toString());
  };

  if (loading) return <div className="loading"><div className="spinner"></div>Carregando...</div>;

  return (
    <div className="caixa-page">
      <div className="caixa-header">
        <h1>💰 Caixa</h1>
        <button
          className="btn btn-outline btn-sm"
          onClick={atualizarAplicacao}
          disabled={atualizando}
          title="Baixa a última versão do sistema sem reiniciar o tablet"
        >
          {atualizando ? '🔄 Atualizando...' : '🔄 Atualizar app'}
        </button>
      </div>

      {!data?.aberto ? (
        <div className="card caixa-fechado">
          <h2>Caixa Fechado</h2>
          <p>Abra o caixa para iniciar as operações do dia.</p>
          <div className="form-group">
            <label>Saldo Inicial (R$)</label>
            <input type="number" step="0.01" value={saldoInicial} onChange={e => setSaldoInicial(e.target.value)} placeholder="0.00" />
          </div>
          <button className="btn btn-success" onClick={abrirCaixa}>🔓 Abrir Caixa</button>
        </div>
      ) : (
        <>
          <div className="caixa-info-grid">
            <div className="card">
              <small>Saldo Inicial</small>
              <strong>{formatCurrency(data.caixa.saldo_inicial)}</strong>
            </div>
            <div className="card">
              <small>Aberto por</small>
              <strong>{data.caixa.usuario_nome}</strong>
            </div>
            <div className="card">
              <small>Saldo Esperado</small>
              <strong>{formatCurrency(saldoEsperado)}</strong>
            </div>
            <div className="card">
              <small>Resumo por Forma</small>
              {resumoFormas.length === 0 && (
                <div className="forma-item"><span style={{ color: '#aaa' }}>Sem vendas ainda</span></div>
              )}
              {resumoFormas.map((r, i) => {
                const bruto = parseFloat(r.total || 0);
                const liquido = parseFloat(r.total_liquido ?? r.total ?? 0);
                const temTaxa = bruto - liquido > 0.005;
                return (
                  <div key={i} className="forma-item">
                    <span>{r.forma_pagamento}</span>
                    {temTaxa ? (
                      <span style={{ textAlign: 'right' }}>
                        <strong>{formatCurrency(liquido)}</strong>
                        <br />
                        <small style={{ color: '#aaa' }}>bruto {formatCurrency(bruto)}</small>
                      </span>
                    ) : (
                      <strong>{formatCurrency(bruto)}</strong>
                    )}
                  </div>
                );
              })}
              {totalTaxasCartao > 0.005 && (
                <>
                  <div className="forma-item" style={{ color: 'var(--danger)' }}>
                    <span>Taxas de cartão</span>
                    <strong>- {formatCurrency(totalTaxasCartao)}</strong>
                  </div>
                  <div className="forma-item" style={{ borderTop: '1px solid #eee', paddingTop: 6 }}>
                    <span>Total líquido</span>
                    <strong>{formatCurrency(totalLiquidoVendas)}</strong>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="caixa-acoes-row">
            <div className="card">
              <h3>💸 Sangria</h3>
              <div className="form-group">
                <input type="number" step="0.01" placeholder="Valor" value={sangria.valor} onChange={e => setSangria({...sangria, valor: e.target.value})} />
              </div>
              <div className="form-group">
                <input placeholder="Motivo" value={sangria.motivo} onChange={e => setSangria({...sangria, motivo: e.target.value})} />
              </div>
              <button className="btn btn-warning btn-sm" onClick={enviarSangria}>Registrar Sangria</button>
            </div>

            <div className="card">
              <h3>💰 Suprimento</h3>
              <div className="form-group">
                <input type="number" step="0.01" placeholder="Valor" value={suprimento.valor} onChange={e => setSuprimento({...suprimento, valor: e.target.value})} />
              </div>
              <div className="form-group">
                <input placeholder="Descrição" value={suprimento.descricao} onChange={e => setSuprimento({...suprimento, descricao: e.target.value})} />
              </div>
              <button className="btn btn-success btn-sm" onClick={enviarSuprimento}>Registrar Suprimento</button>
            </div>
          </div>

          <div className="card" style={{marginTop:16}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
              <h3>📜 Movimentações</h3>
              <button className="btn btn-danger btn-sm" onClick={abrirModalFechar}>🔒 Fechar Caixa</button>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Tipo</th><th>Descrição</th><th>Valor</th><th>Data</th></tr>
                </thead>
                <tbody>
                  {(data.movimentacoes || []).map((m, i) => (
                    <tr key={i}>
                      <td><span className={`badge ${m.tipo === 'venda' ? 'badge-success' : m.tipo === 'sangria' ? 'badge-danger' : 'badge-info'}`}>{m.tipo}</span></td>
                      <td>{m.descricao}</td>
                      <td style={{color: parseFloat(m.valor) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight:600}}>{formatCurrency(m.valor)}</td>
                      <td>{formatDateTime(m.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal Fechamento / Conferência de Caixa */}
          {modalFechar && (
            <div className="modal-overlay" onClick={() => !fechando && (resultadoFechamento ? fecharModalEConcluir() : setModalFechar(false))}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                {!resultadoFechamento ? (
                  <>
                    <h2>🔒 Fechar Caixa</h2>
                    <p style={{ color: 'var(--gray3)', marginTop: -4, marginBottom: 16 }}>
                      Confira os valores e informe quanto foi contado para apurar a diferença.
                    </p>

                    <div className="caixa-conf-linha"><span>Saldo inicial</span><strong>{formatCurrency(saldoInicialAtual)}</strong></div>
                    {resumoFormas.map((r, i) => {
                      const bruto = parseFloat(r.total || 0);
                      const liquido = parseFloat(r.total_liquido ?? r.total ?? 0);
                      const temTaxa = bruto - liquido > 0.005;
                      return (
                        <div key={i} className="caixa-conf-linha caixa-conf-sub">
                          <span>Vendas em {r.forma_pagamento}{temTaxa ? ' (líquido)' : ''}</span>
                          <strong>{formatCurrency(temTaxa ? liquido : bruto)}</strong>
                        </div>
                      );
                    })}
                    {totalTaxasCartao > 0.005 && (
                      <div className="caixa-conf-linha caixa-conf-sub" style={{ color: 'var(--danger)' }}>
                        <span>(-) Taxas de cartão</span>
                        <strong>{formatCurrency(totalTaxasCartao)}</strong>
                      </div>
                    )}
                    <div className="caixa-conf-linha caixa-conf-total">
                      <span>Saldo esperado</span><strong>{formatCurrency(saldoEsperado)}</strong>
                    </div>

                    <div className="form-group" style={{ marginTop: 16 }}>
                      <label>Valor contado (R$)</label>
                      <input type="number" step="0.01" value={valorContado} autoFocus
                        onChange={e => setValorContado(e.target.value)} placeholder="0.00" />
                    </div>

                    {diferencaPrevia !== null && (
                      <div className="caixa-conf-linha caixa-conf-diferenca"
                        style={{ color: Math.abs(diferencaPrevia) < 0.005 ? 'var(--success)' : 'var(--danger)' }}>
                        <span>Diferença</span>
                        <strong>
                          {diferencaPrevia > 0 ? '+' : ''}{formatCurrency(diferencaPrevia)}
                          {Math.abs(diferencaPrevia) < 0.005 ? ' (caixa batido)' : diferencaPrevia > 0 ? ' (sobra)' : ' (falta)'}
                        </strong>
                      </div>
                    )}

                    <div className="form-group" style={{ marginTop: 12 }}>
                      <label>Observações (opcional)</label>
                      <input value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="Ex: sobra de troco, ajuste..." />
                    </div>

                    <div className="modal-actions">
                      <button type="button" className="btn btn-secondary" onClick={() => setModalFechar(false)} disabled={fechando}>Cancelar</button>
                      <button type="button" className="btn btn-danger" onClick={confirmarFecharCaixa} disabled={fechando || valorContado === ''}>
                        {fechando ? 'Fechando...' : 'Confirmar Fechamento'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h2>✅ Caixa Fechado</h2>
                    <div className="caixa-conf-linha caixa-conf-total">
                      <span>Saldo final apurado</span><strong>{formatCurrency(resultadoFechamento.saldo_final)}</strong>
                    </div>
                    {resultadoFechamento.taxas_cartao > 0.005 && (
                      <>
                        <div className="caixa-conf-linha caixa-conf-sub" style={{ color: 'var(--danger)' }}>
                          <span>(-) Taxas de cartão</span><strong>{formatCurrency(resultadoFechamento.taxas_cartao)}</strong>
                        </div>
                        <div className="caixa-conf-linha caixa-conf-total">
                          <span>Total líquido (em conta)</span><strong>{formatCurrency(resultadoFechamento.saldo_final_liquido)}</strong>
                        </div>
                      </>
                    )}
                    <div className="caixa-conf-linha"><span>Valor contado</span><strong>{formatCurrency(parseFloat(valorContado) || 0)}</strong></div>
                    <div className="caixa-conf-linha caixa-conf-diferenca"
                      style={{ color: Math.abs(resultadoFechamento.diferenca) < 0.005 ? 'var(--success)' : 'var(--danger)' }}>
                      <span>Diferença</span>
                      <strong>
                        {resultadoFechamento.diferenca > 0 ? '+' : ''}{formatCurrency(resultadoFechamento.diferenca)}
                        {Math.abs(resultadoFechamento.diferenca) < 0.005 ? ' (caixa batido)' : resultadoFechamento.diferenca > 0 ? ' (sobra)' : ' (falta)'}
                      </strong>
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn btn-primary" onClick={fecharModalEConcluir}>Concluir</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
